import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Loader2, RefreshCw, UserX, Save, CheckCircle2, LifeBuoy, KeyRound } from 'lucide-react';
import {
  UniversalPage,
  UniversalEmptyState,
  Card,
  Button,
  Input,
  StatusBadge,
  Modal,
  ModalBody,
  ModalFooter,
  useToast,
  type StatusTone,
} from '@afrilink/ui';
import {
  salesService,
  type StaffMember,
  type AgentInviteStatus,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';

/**
 * SalesAgentDetailPage — record-scoped drill-down for a single sales agent.
 *
 * Managers (own company) and admins can:
 *   - see invite-acceptance status (invited / accepted / active / deactivated)
 *   - amend name, phone, role, hired date, email
 *   - re-send the invite email
 *   - deactivate the agent (reassigning their open leads)
 */

const STATUS_TONE: Record<AgentInviteStatus, StatusTone> = {
  active: 'active',
  accepted: 'info',
  invited: 'pending',
  pending: 'expiring',
  deactivated: 'draft',
};

const STATUS_LABEL: Record<AgentInviteStatus, string> = {
  active: 'Active — signed in',
  accepted: 'Accepted — not signed in yet',
  invited: 'Invite sent — awaiting acceptance',
  pending: 'Invite not delivered',
  deactivated: 'Deactivated',
};

function fmtDate(v: string | null): string {
  return v ? new Date(v).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}
function fmtDateTime(v: string | null): string {
  return v ? new Date(v).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

export default function SalesAgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useStaffRole();
  const { toast } = useToast();

  const [agent, setAgent] = useState<StaffMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [supportSaving, setSupportSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'sales_agent' | 'sales_manager'>('sales_agent');
  const [hiredAt, setHiredAt] = useState('');

  // Deactivate modal
  const [showDeactivate, setShowDeactivate] = useState(false);

  const hydrateForm = useCallback((a: StaffMember) => {
    setDisplayName(a.display_name ?? '');
    setPhone(a.phone ?? '');
    setEmail(a.email ?? '');
    setRole(a.role === 'sales_manager' ? 'sales_manager' : 'sales_agent');
    setHiredAt(a.hired_at ?? '');
  }, []);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const a = await salesService.getAgent(id);
      setAgent(a);
      if (a) hydrateForm(a);
    } finally {
      setLoading(false);
    }
  }, [id, hydrateForm]);

  useEffect(() => { reload(); }, [reload]);

  // Managers cannot edit admins; nobody edits a role field for an admin target.
  const targetIsAdmin = agent?.role === 'admin';
  const canEdit = agent != null && agent.active && (isAdmin || !targetIsAdmin);

  const dirty = useMemo(() => {
    if (!agent) return false;
    return (
      displayName.trim() !== (agent.display_name ?? '') ||
      (phone || '') !== (agent.phone ?? '') ||
      email.trim().toLowerCase() !== (agent.email ?? '').toLowerCase() ||
      (targetIsAdmin ? false : role !== agent.role) ||
      (hiredAt || '') !== (agent.hired_at ?? '')
    );
  }, [agent, displayName, phone, email, role, hiredAt, targetIsAdmin]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!id || !agent) return;
    setSaving(true);
    setError(null);
    try {
      const patch: Parameters<typeof salesService.updateAgent>[1] = {};
      if (displayName.trim() !== (agent.display_name ?? '')) patch.display_name = displayName.trim();
      if ((phone || '') !== (agent.phone ?? '')) patch.phone = phone.trim() || null;
      if (email.trim().toLowerCase() !== (agent.email ?? '').toLowerCase()) patch.email = email.trim();
      if (!targetIsAdmin && role !== agent.role) patch.role = role;
      if ((hiredAt || '') !== (agent.hired_at ?? '')) patch.hired_at = hiredAt || null;

      const res = await salesService.updateAgent(id, patch);
      toast({
        tone: 'success',
        title: 'Agent updated',
        body: res.invite_sent
          ? 'Changes saved and a fresh invite was emailed.'
          : 'Changes saved.',
      });
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onToggleSupport(enable: boolean) {
    if (!id) return;
    setSupportSaving(true);
    setError(null);
    try {
      await salesService.setSupportAccess(id, enable);
      toast({
        tone: 'success',
        title: enable ? 'Support access granted' : 'Support access removed',
        body: enable
          ? 'The agent can now handle customer support (client operations, no confidential financials). Takes effect on their next sign-in.'
          : 'The agent no longer has support-console access. Their sales role is unchanged.',
      });
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSupportSaving(false);
    }
  }

  async function onResendInvite() {
    if (!id) return;
    setResending(true);
    setError(null);
    try {
      const res = await salesService.resendAgentInvite(id);
      toast({
        tone: res.invite_sent ? 'success' : 'warning',
        title: res.invite_sent ? 'Invite re-sent' : 'Could not send invite',
        body: res.invite_sent
          ? `A new invite email was sent to ${agent?.email ?? 'the agent'}.`
          : 'The invite email failed to send — if the agent already clicked an earlier link, use "Send password reset" instead.',
      });
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setResending(false);
    }
  }

  // 2026-09-07: the action that works in every state. An agent who clicked
  // the old invite link is "confirmed" to GoTrue with no password set, and
  // GoTrue refuses to re-invite a confirmed address — so re-send invite is a
  // dead end for exactly the people who need help. This mints a fresh
  // one-shot set-password link instead.
  async function onSendPasswordReset() {
    if (!id) return;
    setResetting(true);
    setError(null);
    try {
      const res = await salesService.sendAgentPasswordReset(id);
      toast({
        tone: res.reset_sent ? 'success' : 'warning',
        title: res.reset_sent ? 'Password reset sent' : 'Could not send reset',
        body: res.reset_sent
          ? `A set-password link was emailed to ${agent?.email ?? 'the agent'}. It is valid for 60 minutes and only the newest link works — do not send another until they have tried this one.`
          : 'The reset email could not be queued — check the address and the email log.',
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setResetting(false);
    }
  }

  if (loading) {
    return (
      <UniversalPage title="Agent">
        <Card className="p-8 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Loading…
        </Card>
      </UniversalPage>
    );
  }

  if (!agent) {
    return (
      <UniversalPage title="Agent">
        <UniversalEmptyState
          title="Agent not found"
          description="This agent doesn't exist or isn't in your company."
        />
        <div className="mt-4">
          <Button variant="secondary" onClick={() => navigate('/sales/agents')}>
            <ArrowLeft className="h-4 w-4" /> Back to agents
          </Button>
        </div>
      </UniversalPage>
    );
  }

  const notAccepted = agent.invite_status === 'invited' || agent.invite_status === 'pending';

  return (
    <UniversalPage
      title={agent.display_name}
      description={agent.company_name ?? undefined}
      actions={
        <Button variant="secondary" onClick={() => navigate('/sales/agents')}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      }
    >
      {/* ── Status card ─────────────────────────────────────────────── */}
      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <StatusBadge tone={STATUS_TONE[agent.invite_status]}>
              {STATUS_LABEL[agent.invite_status]}
            </StatusBadge>
            <StatusBadge tone={agent.role === 'admin' ? 'overdue' : 'info'}>
              {agent.role.replace('sales_', '').replace('_', ' ')}
            </StatusBadge>
          </div>
          <div className="flex gap-2">
            {agent.active && notAccepted && (
              <Button variant="secondary" onClick={onResendInvite} disabled={resending || resetting}>
                {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Re-send invite
              </Button>
            )}
            {agent.active && (isAdmin || !targetIsAdmin) && (
              <Button variant="secondary" onClick={onSendPasswordReset} disabled={resetting || resending}>
                {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Send password reset
              </Button>
            )}
            {agent.active && (isAdmin || !targetIsAdmin) && (
              <Button variant="destructive" onClick={() => setShowDeactivate(true)}>
                <UserX className="h-4 w-4" /> Deactivate
              </Button>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Invited</dt>
            <dd className="font-medium">{fmtDate(agent.invited_at)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Accepted</dt>
            <dd className="font-medium">{fmtDateTime(agent.email_confirmed_at)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Last sign-in</dt>
            <dd className="font-medium">{fmtDateTime(agent.last_sign_in_at)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Hired</dt>
            <dd className="font-medium">{fmtDate(agent.hired_at)}</dd>
          </div>
        </dl>
      </Card>

      {/* ── Support access (admin only) ─────────────────────────────── */}
      {isAdmin && (
        <Card className="mb-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <LifeBuoy className="mt-0.5 h-5 w-5 text-primary" aria-hidden />
              <div>
                <h2 className="text-sm font-semibold text-navy-primary">Support access</h2>
                <p className="mt-0.5 max-w-xl text-xs text-muted-foreground">
                  {agent.app_role === 'support'
                    ? 'This agent can handle customer support — individual client pages, billing, hosting, domains, edit client details and resend invoices. They cannot see SightHost financial reports or all-sales figures.'
                    : 'Grant access to the support console so this agent can assist customers: individual client pages, per-client billing, resend invoices, edit client details. Excludes confidential financial reports and all-sales figures.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge tone={agent.app_role === 'support' ? 'active' : 'draft'}>
                {agent.app_role === 'support' ? 'Support enabled' : 'No support access'}
              </StatusBadge>
              {agent.app_role === 'support' ? (
                <Button variant="secondary" onClick={() => onToggleSupport(false)} disabled={supportSaving}>
                  {supportSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}
                  Remove support access
                </Button>
              ) : (
                <Button variant="primary" onClick={() => onToggleSupport(true)} disabled={supportSaving}>
                  {supportSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <LifeBuoy className="h-4 w-4" />}
                  Make support agent
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── Edit form ───────────────────────────────────────────────── */}
      <Card className="p-4">
        <h2 className="mb-3 text-sm font-semibold text-navy-primary">Agent details</h2>
        {!canEdit && (
          <div className="mb-3 rounded-md border border-border bg-muted/40 p-2 text-xs text-muted-foreground">
            {targetIsAdmin && !isAdmin
              ? 'This is an admin account — only another admin can change these details.'
              : 'This agent is deactivated. Reactivation is handled by an admin.'}
          </div>
        )}
        <form onSubmit={onSave} className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Display name</span>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={!canEdit} required />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Phone</span>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!canEdit} placeholder="+27…" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Email</span>
            <Input value={email} type="email" onChange={(e) => setEmail(e.target.value)} disabled={!canEdit} required />
            {agent.email && email.trim().toLowerCase() !== agent.email.toLowerCase() && notAccepted && (
              <span className="mt-1 block text-xs text-warning-dark">
                Changing the email of a pending invite will re-send it to the new address.
              </span>
            )}
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Hired date</span>
            <Input value={hiredAt} type="date" onChange={(e) => setHiredAt(e.target.value)} disabled={!canEdit} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Role</span>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
              value={targetIsAdmin ? 'admin' : role}
              onChange={(e) => setRole(e.target.value as typeof role)}
              disabled={!canEdit || targetIsAdmin}
            >
              {targetIsAdmin && <option value="admin">admin</option>}
              <option value="sales_agent">Sales agent</option>
              <option value="sales_manager">Sales manager</option>
            </select>
          </label>

          {error && (
            <div className="sm:col-span-2 rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">
              {error}
            </div>
          )}

          {canEdit && (
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button type="submit" variant="primary" disabled={saving || !dirty}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save changes
              </Button>
              {!dirty && <span className="text-xs text-muted-foreground">No unsaved changes</span>}
            </div>
          )}
        </form>
      </Card>

      <DeactivateModal
        open={showDeactivate}
        agent={agent}
        isAdmin={isAdmin}
        onClose={() => setShowDeactivate(false)}
        onDone={() => { setShowDeactivate(false); navigate('/sales/agents'); }}
      />
    </UniversalPage>
  );
}

/** Deactivation requires a reassignment target for the agent's open leads. */
function DeactivateModal({
  open,
  agent,
  isAdmin,
  onClose,
  onDone,
}: {
  open: boolean;
  agent: StaffMember;
  isAdmin: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [candidates, setCandidates] = useState<StaffMember[]>([]);
  const [reassignTo, setReassignTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    salesService
      .listAgents(isAdmin ? undefined : agent.company_id)
      .then((rows) =>
        setCandidates(
          rows.filter((r) => r.id !== agent.id && r.active && r.role !== 'admin'),
        ),
      )
      .catch(() => { /* surfaced on submit */ });
  }, [open, agent.id, agent.company_id, isAdmin]);

  async function onConfirm() {
    if (!reassignTo) { setError('Choose an agent to receive the open leads.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await salesService.deactivateStaff(agent.id, reassignTo);
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Deactivate ${agent.display_name}?`}>
      <ModalBody>
        <p className="text-sm text-muted-foreground">
          The agent loses portal access immediately. Their open leads must be
          reassigned to another active agent.
        </p>
        <label className="mt-3 block text-sm">
          <span className="mb-1 block text-muted-foreground">Reassign open leads to</span>
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={reassignTo}
            onChange={(e) => setReassignTo(e.target.value)}
          >
            <option value="" disabled>Select agent…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>{c.display_name}</option>
            ))}
          </select>
        </label>
        {candidates.length === 0 && (
          <p className="mt-2 text-xs text-warning-dark">
            No other active agents available to receive leads.
          </p>
        )}
        {error && (
          <div className="mt-2 rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">{error}</div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="destructive" onClick={onConfirm} disabled={submitting || !reassignTo}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Deactivate
        </Button>
      </ModalFooter>
    </Modal>
  );
}
